import { t } from '@glory/i18n';
import {
  Button,
  ContentSwap,
  Dialog,
  InfoHint,
  PageHeader,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@glory/ui';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Plus } from 'lucide-react';

import { MyTaskList, ReviewQueue, TimeDialog } from '../../features/projects/ui/my-tasks.tsx';
import { Timesheet } from '../../features/projects/ui/timesheet.tsx';
import { useCrumbs } from '../../lib/crumbs.tsx';
import { requirePermissionRoute, usePermissions } from '../../lib/permissions.ts';

const TABS = ['review', 'time'] as const;
const DIALOGS = ['timer', 'log'] as const;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

interface TasksSearch {
  /** Без значения — «Мои». */
  readonly tab?: (typeof TABS)[number] | undefined;
  /** Неделя табеля: любой её день; без значения — текущая. */
  readonly week?: string | undefined;
  /** Окно из ⌘K: «Запустить таймер на…» или «Внести время». */
  readonly dialog?: (typeof DIALOGS)[number] | undefined;
}

export const Route = createFileRoute('/_app/tasks')({
  validateSearch: (search: Record<string, unknown>): TasksSearch => ({
    tab: TABS.find((tab) => tab === search.tab),
    week: typeof search.week === 'string' && ISO_DATE.test(search.week) ? search.week : undefined,
    dialog: DIALOGS.find((dialog) => dialog === search.dialog),
  }),
  beforeLoad: requirePermissionRoute('projects.use'),
  component: TasksPage,
});

/**
 * «Задачи» (раздел 9): «Мои» по срокам, «На проверке у меня» — у руководителей, «Время» — недельный табель.
 * Вкладка, неделя и окно из ⌘K — в адресе.
 */
function TasksPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { can } = usePermissions();
  const leads = can('projects.lead') || can('projects.manage');
  const tab = search.tab === 'review' && !leads ? undefined : search.tab;
  useCrumbs(
    tab === 'review'
      ? [{ label: t('projects.my.tabReview') }]
      : tab === 'time'
        ? [{ label: t('projects.my.tabTime') }]
        : null,
  );
  const setSearch = (patch: Partial<TasksSearch>) => {
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  };

  return (
    <Tabs
      // min-w-0: широкий табель прокручивается внутри, а не раздвигает страницу шире экрана.
      className="flex min-h-full w-full min-w-0 flex-col gap-4 px-4 py-5 md:px-6"
      value={tab ?? 'mine'}
      onValueChange={(next) => {
        setSearch({ tab: TABS.find((own) => own === next) });
      }}
    >
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            {t('projects.my.title')}
            <InfoHint hint={{ text: t('projects.my.hint') }} />
          </span>
        }
        actions={
          <Button
            onClick={() => {
              setSearch({ dialog: 'log' });
            }}
          >
            <Plus /> {t('projects.time.add')}
          </Button>
        }
      />
      <TabsList>
        <TabsTrigger value="mine">{t('projects.my.tabMine')}</TabsTrigger>
        {leads ? <TabsTrigger value="review">{t('projects.my.tabReview')}</TabsTrigger> : null}
        <TabsTrigger value="time">{t('projects.my.tabTime')}</TabsTrigger>
      </TabsList>
      <ContentSwap swapKey={tab ?? 'mine'}>
        <TabsContent value="mine">
          <MyTaskList />
        </TabsContent>
        {leads ? (
          <TabsContent value="review">
            <ReviewQueue />
          </TabsContent>
        ) : null}
        <TabsContent value="time">
          <Timesheet
            weekOf={search.week}
            onWeek={(week) => {
              setSearch({ week });
            }}
          />
        </TabsContent>
      </ContentSwap>
      <Dialog
        open={search.dialog !== undefined}
        onOpenChange={(open) => {
          if (!open) setSearch({ dialog: undefined });
        }}
      >
        {search.dialog ? (
          <TimeDialog
            mode={search.dialog}
            onDone={() => {
              setSearch({ dialog: undefined });
            }}
          />
        ) : null}
      </Dialog>
    </Tabs>
  );
}
